import React, { useState } from 'react';
import TriggerBox from './TriggerBox';
import FeedbackSelector from './FeedbackSelector';
import ThumbsSelector from './ThumbsSelector';
import { UNDERWRITING_CONFIG, getValueFromPath } from './config/underwritingFeedbackConfig';

/**
 * Reusable Feedback Card Component
 * 
 * Renders a section card with:
 * - Main grade (if applicable)
 * - Explanation (always visible)
 * - Trigger boxes (collapsible)
 * - Tool execution indicators
 * - Inline MCQ feedback selector
 */
const FeedbackCard = ({ config, data, feedback, onFeedbackChange, allData }) => {
  const [isTriggersExpanded, setIsTriggersExpanded] = useState(false);
  const { styling } = UNDERWRITING_CONFIG;

  // Check if this is a tool-based section
  const isToolSection = config.toolName && config.requiredPath;
  let toolExecuted = false;
  let toolRationale = '';
  
  if (isToolSection) {
    toolExecuted = getValueFromPath(allData, config.requiredPath) === true;
    
    // Get tool rationale from tool_selector thought trace
    const toolEvaluation = getValueFromPath(
      allData, 
      `tool_selector_module.raw_output.thought_trace.tool_evaluation.${config.toolName}`
    );
    toolRationale = toolEvaluation || '';
  }

  // Check if this is the overall decision card
  const isOverallDecision = config.id === 'overall_risk';
  
  // Get explanation text - check if path is from root or section
  let explanationText = null;
  if (config.explanation) {
    const sourceData = config.explanation.source === 'root' ? allData : data;
    const rawData = getValueFromPath(sourceData, config.explanation.dataPath);
    
    // Apply formatter if available
    if (config.explanation.formatter && typeof config.explanation.formatter === 'function') {
      explanationText = config.explanation.formatter(rawData);
    } else {
      explanationText = rawData;
    }
  }
  
  // Get risk grade value for right card
  let riskGradeValue = null;
  if (config.displayGrade && config.displayGrade.dataPath) {
    // Check displayGrade first (for tool sections)
    riskGradeValue = getValueFromPath(allData, config.displayGrade.dataPath);
  } else if (config.mainGrade) {
    // Regular sections with mainGrade
    riskGradeValue = getValueFromPath(data, config.mainGrade.dataPath);
  } else if (config.fields && config.fields.length > 0 && config.fields[0].displayType === 'badge') {
    // Only use first field if it's a badge type (like overall_risk)
    riskGradeValue = getValueFromPath(data, config.fields[0].dataPath);
  }
  // If none of the above, riskGradeValue remains null and won't display
  
  // Get decision value (for overall only)
  const decisionValue = isOverallDecision 
    ? getValueFromPath(allData, 'final_decision.decision')
    : null;

  // Empty state for non-tool sections
  if (!data && !isToolSection) {
    return (
      <div className="feedback-row">
        <div className="feedback-card-wrapper">
          <div className={`feedback-card-left ${!config.feedback ? 'full-width' : ''}`}>
            <div className="card-header">
              <h3 className="card-title">{config.title}</h3>
            </div>
            <p className="no-data-message">No data available for this section</p>
          </div>
          {config.feedback && (
            <div className="risk-grade-card">
              <div className="risk-grade-label">{config.feedback.label || 'Risk Grade'}</div>
              <div className="risk-grade-value">-</div>
              <ThumbsSelector
                value={feedback}
                onChange={onFeedbackChange}
                required={config.feedback?.required}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Special rendering for Overall Decision card
  if (isOverallDecision) {
    // Get decision color for outer wrapper
    const decisionColor = decisionValue === 'APPROVE' ? '#28a745' : '#dc3545';
    const decisionTint = decisionValue === 'APPROVE' 
      ? 'rgba(40, 167, 69, 0.05)'
      : 'rgba(220, 53, 69, 0.05)';
    
    return (
      <div 
        className="overall-decision-wrapper"
        style={{
          borderColor: decisionColor,
          background: `linear-gradient(135deg, ${decisionTint} 0%, #fff5ed 100%)`
        }}
      >
        <div className="overall-decision-card-left">
          {/* Header */}
          <div className="card-header">
            <h3 className="card-title">{config.title}</h3>
          </div>

          {/* Risk Grade Display */}
          {config.fields && config.type === 'single_value' && (
            <div className="fields-section">
              {config.fields.map(field => {
                const value = getValueFromPath(data, field.dataPath);
                return (
                  <div key={field.key} className="field-item">
                    <span className="field-label">{field.label}:</span>
                    <span 
                      className={`field-value ${field.displayType}`}
                      style={{
                        color: UNDERWRITING_CONFIG.riskColors[value] || '#000'
                      }}
                    >
                      {value || '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Explanation */}
          {explanationText && (
            <div className="explanation-section-visible">
              <div className="explanation-label">{config.explanation.label}</div>
              <div className="explanation-text">{explanationText}</div>
            </div>
          )}
        </div>

        {/* RIGHT PANE: Combined Risk Grade + Decision */}
        {config.feedback && (
          <div className="overall-decision-right-pane">
            {/* Combined Assessment Section */}
            <div 
              className="combined-assessment-section"
              style={{
                backgroundColor: `${UNDERWRITING_CONFIG.riskColors[riskGradeValue] || '#6c757d'}10`,
                borderColor: UNDERWRITING_CONFIG.riskColors[riskGradeValue] || '#6c757d'
              }}
            >
              <div className="pane-label">{config.feedback.label}</div>
              
              {/* Risk Grade + Decision Combined */}
              <div className="risk-decision-combined">
                <div className="combined-item">
                  <span className="combined-label">Risk:</span>
                  <span 
                    className="combined-value"
                    style={{
                      color: '#000000'
                    }}
                  >
                    {riskGradeValue || '-'}
                  </span>
                </div>
                
                {decisionValue && (
                  <div className="combined-item">
                    <span className="combined-label">Decision:</span>
                    <span 
                      className="combined-value combined-decision"
                      style={{
                        color: decisionColor
                      }}
                    >
                      {decisionValue}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Thumbs attached to combined element */}
              <ThumbsSelector
                value={feedback}
                onChange={onFeedbackChange}
                required={config.feedback?.required}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular card rendering
  return (
    <div className="feedback-row">
      <div className={`feedback-card-wrapper ${isToolSection && !toolExecuted ? 'tool-not-executed' : ''}`}>
        {/* LEFT SIDE: Content Card (80%) */}
        <div className={`feedback-card-left ${!config.feedback ? 'full-width' : ''}`}>
        
        {/* Header with tool indicator */}
        <div className="card-header">
          <h3 className="card-title">{config.title}</h3>
          
          {/* Tool execution indicator */}
          {isToolSection && (
            <span className={`tool-indicator ${toolExecuted ? 'executed' : 'not-executed'}`}>
              {toolExecuted ? '✓ Considered for Overall Risk' : '✗ Not Considered for Overall Risk'}
            </span>
          )}
        </div>

        {/* Show content only if not tool-section OR if tool was executed */}
        {(!isToolSection || toolExecuted) && (
          <>
            {/* Explanation - ALWAYS VISIBLE (Risk grade moved to right card) */}
            {explanationText && (
              <div className="explanation-section-visible">
                <div className="explanation-label">{config.explanation.label}</div>
                {/* Show as bullets if it's an array, otherwise as text */}
                {Array.isArray(explanationText) ? (
                  <ul className="explanation-bullets">
                    {explanationText.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : typeof explanationText === 'string' ? (
                  <ul className="explanation-bullets">
                    {explanationText.split(/[.!?]+/).filter(s => s.trim()).map((sentence, index) => (
                      <li key={index}>{sentence.trim()}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="explanation-text">{explanationText}</div>
                )}
              </div>
            )}

            {/* Triggers/Metrics - COLLAPSIBLE */}
            {config.triggers && (
              <div className="triggers-collapsible-section">
                <button
                  className="triggers-toggle"
                  onClick={() => setIsTriggersExpanded(!isTriggersExpanded)}
                >
                  <span>More details</span>
                  <span className={`toggle-icon ${isTriggersExpanded ? 'expanded' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {isTriggersExpanded && (
                  <div className="triggers-grid">
                    {config.triggers.map(trigger => {
                      const value = getValueFromPath(data, trigger.dataPath);
                      return (
                        <TriggerBox
                          key={trigger.key}
                          label={trigger.label}
                          value={value}
                          nullable={trigger.nullable}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Fields for PD/Business Credit sections */}
            {config.fields && config.type !== 'single_value' && (
              <div className="fields-grid">
                {config.fields.map(field => {
                  const value = getValueFromPath(data, field.dataPath);
                  let displayValue = value;
                  
                  // Format based on display type
                  if (field.displayType === 'currency' && value !== null) {
                    displayValue = new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      maximumFractionDigits: 0
                    }).format(value);
                  }
                  
                  return (
                    <div 
                      key={field.key} 
                      className="field-box"
                      style={{ borderRadius: styling.triggerBoxRadius }}
                    >
                      <div className="field-box-label">{field.label}</div>
                      <div className="field-box-value">{displayValue || '-'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Show rationale if tool was NOT executed */}
        {isToolSection && !toolExecuted && (
          <div className="tool-not-executed-rationale">
            <p>{toolRationale || 'Tool was not required for this case.'}</p>
          </div>
        )}

        {/* Show rationale if tool WAS executed */}
        {isToolSection && toolExecuted && toolRationale && (
          <div className="tool-executed-rationale">
            <p>{toolRationale}</p>
          </div>
        )}
        </div>

        {/* RIGHT SIDE: Risk Grade Card (20%) */}
        {config.feedback && (
          <div 
            className="risk-grade-card"
            style={{
              backgroundColor: `${UNDERWRITING_CONFIG.riskColors[riskGradeValue] || '#6c757d'}15`,
              borderColor: UNDERWRITING_CONFIG.riskColors[riskGradeValue] || '#6c757d'
            }}
          >
            <div className="risk-grade-label">{config.feedback.label}</div>
            <div 
              className="risk-grade-value"
              style={{
                color: '#000000'
              }}
            >
              {riskGradeValue || null}
            </div>
            <ThumbsSelector
              value={feedback}
              onChange={onFeedbackChange}
              required={config.feedback?.required}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackCard;
